import { formatDate as formatUiDate, useTranslation } from '@hbs-us/ui';
import { Link } from '@tanstack/react-router';

import { sqlUtcToIso } from '../../../lib/sqlTimestamp.js';
import type { MoneyFormatters } from '../../_money/useMoneyFormatters.js';
import type { EstimateListItemDto } from '../contractsMirror.js';
import { EstimateStatusBadge } from './EstimateStatusBadge.js';

/**
 * Estimates list table (legacy `EstimatesListPage` table). A real semantic
 * `<table>` — Estimate (number) · Customer · Status (badge) · **Total** (right,
 * mono) · Updated · Actions (View / Edit) — with a `<caption>`, `<th scope="col">`
 * headers, the numeric Total right-aligned, and a readable colspan empty row
 * (A11Y-01). Numerals in IBM Plex Mono. Every color is a locked token.
 *
 * The estimate-number cell links to the detail route. Edit is offered for
 * draft/ready estimates (legacy parity — only editable before send/approval).
 */

const TEXT_TH =
  'px-3 py-2 text-left font-semibold text-muted-foreground text-xs uppercase tracking-wide';
const NUMERIC_TH =
  'px-3 py-2 text-right font-semibold text-muted-foreground text-xs uppercase tracking-wide';

function Dash() {
  return <span className="text-muted-foreground">—</span>;
}

/** Draft/ready estimates are still editable (legacy parity). */
function isEditable(status: EstimateListItemDto['status']): boolean {
  return status === 'draft' || status === 'ready';
}

export function EstimatesTable({
  estimates,
  fmt,
}: {
  estimates: EstimateListItemDto[];
  fmt: MoneyFormatters;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';

  return (
    <div className="overflow-x-auto rounded-md border border-border bg-card">
      <table className="w-full border-collapse">
        <caption className="sr-only">{t('estimates:table.caption')}</caption>
        <thead>
          <tr className="border-border border-b">
            <th scope="col" className={TEXT_TH}>
              {t('estimates:table.estimate')}
            </th>
            <th scope="col" className={TEXT_TH}>
              {t('estimates:table.customer')}
            </th>
            <th scope="col" className={TEXT_TH}>
              {t('estimates:table.status')}
            </th>
            <th scope="col" className={NUMERIC_TH}>
              {t('estimates:table.total')}
            </th>
            <th scope="col" className={TEXT_TH}>
              {t('estimates:table.updated')}
            </th>
            <th scope="col" className={NUMERIC_TH}>
              {t('estimates:table.actions')}
            </th>
          </tr>
        </thead>
        <tbody>
          {estimates.map((est) => (
            <tr key={est.id} className="border-border/60 border-b last:border-b-0">
              <td className="px-3 py-2 text-sm">
                <Link
                  to="/estimates/$id"
                  params={{ id: String(est.id) }}
                  className="font-semibold font-mono text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                  {est.estimateNumber || t('estimates:table.unnamedEstimate', { id: est.id })}
                </Link>
              </td>
              <td className="px-3 py-2 text-foreground text-sm">
                {est.customerName || t('estimates:table.noCustomer')}
              </td>
              <td className="px-3 py-2 text-sm">
                <EstimateStatusBadge status={est.status} />
              </td>
              <td className="px-3 py-2 text-right font-mono font-medium text-foreground text-sm">
                {fmt.formatCurrency(est.total || 0)}
              </td>
              <td className="whitespace-nowrap px-3 py-2 font-mono text-foreground text-sm">
                {est.updated ? (
                  formatUiDate(sqlUtcToIso(est.updated), locale, { dateStyle: 'medium' })
                ) : (
                  <Dash />
                )}
              </td>
              <td className="px-3 py-2 text-right">
                <div className="flex flex-wrap items-center justify-end gap-1">
                  <Link
                    to="/estimates/$id"
                    params={{ id: String(est.id) }}
                    className="inline-flex h-11 items-center rounded-md px-3 font-medium text-foreground text-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  >
                    {t('estimates:actions.view')}
                  </Link>
                  {isEditable(est.status) ? (
                    <Link
                      to="/estimates/$id/edit"
                      params={{ id: String(est.id) }}
                      className="inline-flex h-11 items-center rounded-md px-3 font-medium text-foreground text-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                    >
                      {t('estimates:actions.editShort')}
                    </Link>
                  ) : null}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
